/**
 * Crash recovery helpers shared by app/error.tsx and app/global-error.tsx.
 *
 * The dominant client-side crash in production is a stale-deploy tab: the
 * user loaded the app before a deploy, then navigated after it, and the
 * browser 404s on a JS chunk whose hashed filename no longer exists. A
 * single hard reload fixes it (the reload fetches the new HTML with the new
 * chunk names). We reload automatically, at most once per short window, so
 * the user sees a blink instead of the Next.js death screen.
 */

import { telemetry } from "@/lib/telemetry";

const RELOAD_FLAG = "xynth_crash_reload_at";
// If we already auto-reloaded within this window, don't loop — show the
// fallback UI instead so a genuinely broken build can't reload forever.
const RELOAD_LOOP_WINDOW_MS = 60_000;

export function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  const name = err?.name ?? "";
  const message = err?.message ?? "";
  return (
    name === "ChunkLoadError" ||
    /loading chunk .* failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /css chunk .* failed/i.test(message)
  );
}

export function reportCrash(
  error: unknown,
  opts: { boundary: "root" | "global"; digest?: string; autoReloading: boolean },
): void {
  try {
    const err = error as { name?: string; message?: string; stack?: string } | null;
    telemetry.emit("app.crash", {
      boundary: opts.boundary,
      error_name: err?.name ?? "unknown",
      error_message: (err?.message ?? String(error)).slice(0, 500),
      // 4000 chars so deep React #185 stacks keep their app-code frames —
      // with productionBrowserSourceMaps on, these offsets symbolicate to
      // real file:line via the deployed .map files.
      error_stack: (err?.stack ?? "").slice(0, 4000),
      error_digest: opts.digest,
      is_chunk_load_error: isChunkLoadError(error),
      auto_reloading: opts.autoReloading,
    });
  } catch {
    // Crash reporting must never make the crash worse.
  }
}

/**
 * Reload once for ANY crash. Returns true when a reload was triggered
 * (caller should render nothing / a blank state).
 *
 * Originally chunk-load-only, widened 2026-08-01: prod telemetry showed
 * every React #185 (render stampede on mobile during streaming) victim
 * recovered by reloading — the session renders fine at rest. First crash
 * reloads silently; a second crash within RELOAD_LOOP_WINDOW_MS falls
 * through to the visible retry card so broken builds can't reload forever.
 */
export function maybeAutoReload(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_LOOP_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable: only reload for chunk errors (very likely
    // transient) — for anything else the loop risk outweighs the blink.
    if (!isChunkLoadError(error)) return false;
  }
  window.location.reload();
  return true;
}
